import { AddOutlined, UploadFileOutlined } from '@mui/icons-material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { Alert, Box, CardHeader, Stack, Tooltip } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CustomButton from '@/components/inputs/CustomButton'
import { useIsAdmin } from '@/features/authentication/contexts/AuthContext'
import BulkImportRampCurvesModal from '@/features/resource/components/rampCurves/BulkImportRampCurvesModal'
import RampCurveForm from '@/features/resource/components/rampCurves/RampCurveForm'
import { RampCurvesDataGrid } from '@/features/resource/components/rampCurves/RampCurvesDataGrid'
import RampCurvesTooltipContent from '@/features/resource/components/rampCurves/RampCurvesTooltipContent'
import { GET_RESOURCE_API_ID, GET_RESOURCE_RAMP_CURVES_API_ID } from '@/features/resource/endpoints/resources'
import { useAddRampCurveMutation } from '@/features/resource/hooks/useAddRampCurveMutation'
import { useDeleteRampCurveMutation } from '@/features/resource/hooks/useDeleteRampCurveMutation'
import { useUpdateRampCurveMutation } from '@/features/resource/hooks/useUpdateRampCurveMutation'
import type { AddRampCurveRequest, RampCurve, Resource, UpdateRampCurveRequest } from '@/features/resource/types'

export type RampCurvesPanelProps = {
  resource: Resource
  rampCurves: RampCurve[]
  onRampCurvesChange: (rampCurves: RampCurve[]) => void
}

const RampCurvesPanel = ({ resource, rampCurves, onRampCurvesChange }: RampCurvesPanelProps) => {
  const { t } = useTranslation()
  const [isAddingNewRampCurve, setIsAddingNewRampCurve] = useState(false)
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false)

  const queryClient = useQueryClient()
  const isAdmin = useIsAdmin()

  const { addRampCurve } = useAddRampCurveMutation(resource.resourceID)
  const { updateRampCurve } = useUpdateRampCurveMutation(resource.resourceID)
  const { deleteRampCurve } = useDeleteRampCurveMutation(resource.resourceID)

  const handleAddRampCurve = async (data: AddRampCurveRequest) => {
    try {
      const tempId = 'temp-' + Date.now()
      const newRampCurve: RampCurve = {
        id: tempId,
        verifiedAt: data.verifiedAt,
        name: data.name,
        meteringPortId: data.meteringPortId,
        direction: data.direction,
        rampCurveEntries: data.rampCurveEntries.map((entry, index) => ({
          id: `${tempId}-entry-${index}`,
          durationSinceStartMillis: entry.durationSinceStartMillis,
          consumption: entry.consumption,
        })),
      }
      onRampCurvesChange([...rampCurves, newRampCurve])
      setIsAddingNewRampCurve(false)

      await addRampCurve(data)

      // Delayed refetch to allow backend Pulsar event processing
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: [GET_RESOURCE_API_ID, { id: resource.resourceID }] })
        queryClient.refetchQueries({
          queryKey: [GET_RESOURCE_RAMP_CURVES_API_ID, { resourceId: resource.resourceID }],
        })
      }, 2000)
    } catch (error) {
      console.error('Failed to add ramp curve:', error)
    }
  }

  const handleUpdateRampCurve = async (rampCurveId: string, data: UpdateRampCurveRequest) => {
    try {
      onRampCurvesChange(
        rampCurves.map((curve) => {
          if (curve.id === rampCurveId) {
            return {
              ...curve,
              name: data.name,
              meteringPortId: data.meteringPortId,
              verifiedAt: data.verifiedAt,
              direction: data.direction,
              rampCurveEntries: data.rampCurveEntries.map((entry, index) => ({
                id: `${rampCurveId}-entry-${index}`,
                durationSinceStartMillis: entry.durationSinceStartMillis,
                consumption: entry.consumption,
              })),
            }
          }
          return curve
        }),
      )

      await updateRampCurve({ rampCurveId, data })

      // Delayed refetch to allow backend Pulsar event processing
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: [GET_RESOURCE_API_ID, { id: resource.resourceID }] })
        queryClient.refetchQueries({
          queryKey: [GET_RESOURCE_RAMP_CURVES_API_ID, { resourceId: resource.resourceID }],
        })
      }, 2000)
    } catch (error) {
      console.error('Failed to update ramp curve:', error)
    }
  }

  const handleDeleteRampCurve = async (rampCurveId: string) => {
    try {
      onRampCurvesChange(rampCurves.filter((curve) => curve.id !== rampCurveId))

      await deleteRampCurve(rampCurveId)

      // Delayed refetch to allow backend Pulsar event processing
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: [GET_RESOURCE_API_ID, { id: resource.resourceID }] })
        queryClient.refetchQueries({
          queryKey: [GET_RESOURCE_RAMP_CURVES_API_ID, { resourceId: resource.resourceID }],
        })
      }, 2000)
    } catch (error) {
      console.error('Failed to delete ramp curve:', error)
    }
  }

  const handleBulkImportSuccess = async () => {
    // Bulk import is async on the backend - BFF syncs via Pulsar events after a few seconds.
    // We can't optimistically update local state (unknown what was imported), so we schedule
    // a delayed refetch after the backend has likely processed the event (~2s).
    // Existing polling mechanism will catch any stragglers if this timing isn't sufficient.
    setTimeout(() => {
      queryClient.refetchQueries({
        queryKey: [GET_RESOURCE_RAMP_CURVES_API_ID, { resourceId: resource.resourceID }],
      })
      queryClient.refetchQueries({
        queryKey: [GET_RESOURCE_API_ID, { id: resource.resourceID }],
      })
    }, 2000)
  }

  const handleAddRampCurveClick = () => setIsAddingNewRampCurve(true)
  const handleBulkImportClick = () => setIsBulkImportModalOpen(true)

  return (
    <Stack>
      <CardHeader
        title={
          <Stack alignItems="center" direction="row" spacing={1}>
            <CustomTypography variant="h6">{t('resources.ramp_curves.label')}</CustomTypography>
            <Tooltip arrow placement="right-start" title={<RampCurvesTooltipContent />}>
              <HelpOutlineIcon fontSize="small" sx={{ color: 'black' }} />
            </Tooltip>
          </Stack>
        }
      />
      {rampCurves.length > 0 ? (
        <RampCurvesDataGrid
          rampCurves={rampCurves}
          onDeleteRampCurve={handleDeleteRampCurve}
          onUpdateRampCurve={handleUpdateRampCurve}
        />
      ) : (
        !isAddingNewRampCurve && <Alert severity="info">{t('resources.ramp_curves.info_empty')}</Alert>
      )}
      {isAddingNewRampCurve && (
        <Box mt={2}>
          <RampCurveForm onCancel={() => setIsAddingNewRampCurve(false)} onSave={handleAddRampCurve} />
        </Box>
      )}
      <Box display="flex" gap={2} justifyContent="flex-start" mt={2}>
        <CustomButton
          disabled={isAddingNewRampCurve}
          startIcon={<AddOutlined />}
          sx={{ mt: 2 }}
          variant="outlined"
          onClick={handleAddRampCurveClick}
        >
          {t('resources.ramp_curves.add')}
        </CustomButton>
        {isAdmin && (
          <CustomButton
            startIcon={<UploadFileOutlined />}
            sx={{ mt: 2 }}
            variant="outlined"
            onClick={handleBulkImportClick}
          >
            {t('resources.ramp_curves.bulk_import.button')}
          </CustomButton>
        )}
      </Box>
      <BulkImportRampCurvesModal
        open={isBulkImportModalOpen}
        onClose={() => setIsBulkImportModalOpen(false)}
        onSuccess={handleBulkImportSuccess}
      />
    </Stack>
  )
}

export default RampCurvesPanel
